import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { pageFlowConfigurationSelector } from '@redux/slices/workflowSlice'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { InsurancePlanType } from '@typesApp/InsurancePlan'
import {
  ModalOptions,
  FooterConfiguration,
  ViewName,
  ViewOptions,
  TooltipConfiguration,
  RxHelpGuideOptions,
} from '@typesApp/PageFlow'
import { ROUTES } from '@constants/index'
import { PageFlowUtil } from '@utils/pageFlowUtil'

type ViewConfig = {
  next: string
  previous: string
}

type UsePageFlowResult = {
  isReady: boolean
  defaultView: string
  loginRedirectView: string
  maxSteps: number
  showSteps: boolean
  modalOptions?: ModalOptions
  tooltip?: TooltipConfiguration
  benefitPlanConfiguration?: {
    displayOrder: InsurancePlanType[]
  }
  rxhelpGuideOptions?: RxHelpGuideOptions

  /**
   * @returns navigation configuration (next, previous) for this view
   */
  getViewConfig: (view: ViewName) => ViewConfig

  /**
   * @returns current step for the view, taking into account the dynamic insurance forms.
   * Note: insurance information view should NOT use this api
   */
  getStepForView: (view: ViewName) => number

  /**
   * @returns view to resume to for in progress claim record or null if not configured
   */
  getResumeToView: (claimRecord: ClaimRecord) => ViewName | null

  /**
   * @returns footer configuration (for cta styling) for this view
   */
  getFooterConfiguration: (view: ViewName) => FooterConfiguration | null

  /**
   * @returns additional options for this view
   */
  getOptions: (view: ViewName) => ViewOptions | null
}

export const usePageFlow = (): UsePageFlowResult => {
  const config = useSelector(pageFlowConfigurationSelector)

  const result = useMemo<UsePageFlowResult>(() => {
    return {
      isReady: Boolean(config),
      maxSteps: config?.maxSteps || 0,
      showSteps: config?.showSteps === undefined ? true : config.showSteps,
      defaultView: toRoutePath(config?.defaultView) || ROUTES.BASE,
      loginRedirectView: toRoutePath(config?.loginRedirectView) || ROUTES.BASE,
      modalOptions: config?.modalOptions,
      tooltip: config?.tooltip,
      rxhelpGuideOptions: config?.rxhelpGuide,
      benefitPlanConfiguration: config?.benefitPlanConfiguration,
      getViewConfig: (view: ViewName) => {
        return {
          next: toRoutePath(config?.flow[view]?.next),
          previous: toRoutePath(config?.flow[view]?.previous),
        }
      },
      getStepForView: (view: ViewName): number => {
        if (!config?.steps) return 0
        return config.steps.indexOf(view) + (config?.stepsOffset || 0)
      },
      getResumeToView: (claimRecord: ClaimRecord): ViewName | null => {
        const mapping = config?.resumeMapping
        if (!mapping) {
          return null
        }
        return PageFlowUtil.getResumeToView(config, claimRecord)
      },
      getFooterConfiguration: (view: ViewName) => PageFlowUtil.getFooterConfiguration(config, view),
      getOptions: (view: ViewName): ViewOptions | null => (config?.options ? config.options[view] : null),
    }
  }, [config])

  return result
}

const toRoutePath = (pageRoute: ViewName | undefined): string => {
  return !pageRoute ? '' : pageRoute === 'intro' ? ROUTES.BASE : pageRoute
}
